import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { CoordPicker } from '../components/CoordPicker.tsx';
import { SuggestField } from '../components/SuggestField.tsx';

const text = (v: string) => (v.trim() ? v.trim() : null);
const num = (v: string) => (v.trim() ? Number(v.replace(',', '.')) : null);

/**
 * Выезд — место и обстоятельства для целой партии образцов. Правка
 * координат здесь меняет положение всех привезённых образцов сразу:
 * в этом и смысл сущности (ТЗ 2.5).
 */
export function TripFormScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [title, setTitle] = useState('');
  const [startedOn, setStartedOn] = useState(new Date().toISOString().slice(0, 10));
  const [localityName, setLocalityName] = useState('');
  const [country, setCountry] = useState('');
  const [localityNote, setLocalityNote] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [precision, setPrecision] = useState('');
  const [companions, setCompanions] = useState('');
  const [notes, setNotes] = useState('');

  const [specimenCount, setSpecimenCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!id) return;
    api
      .trip(id)
      .then((t) => {
        setTitle(t.title ?? '');
        setStartedOn(t.started_on.slice(0, 10));
        setLocalityName(t.locality_name ?? '');
        setCountry(t.locality_country ?? '');
        setLocalityNote(t.locality_note ?? '');
        setLatitude(t.latitude ? String(Number(t.latitude)) : '');
        setLongitude(t.longitude ? String(Number(t.longitude)) : '');
        setPrecision(t.coord_precision_m != null ? String(t.coord_precision_m) : '');
        setCompanions(t.companions ?? '');
        setNotes(t.notes ?? '');
        setSpecimenCount(t.specimen_count);
        setLoaded(true);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoaded(true);
      });
  }, [id]);

  const localitySuggestions = useCallback(
    (q: string) =>
      api.localitySuggestions(q).then((rows) =>
        rows.map((r) => ({ key: String(r.id), title: r.name, rank: r.country ?? undefined })),
      ),
    [],
  );

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        title: text(title),
        started_on: startedOn,
        locality_name: text(localityName),
        locality_country: text(country),
        locality_note: text(localityNote),
        latitude: num(latitude),
        longitude: num(longitude),
        // Точка выезда ставится руками — картой, числами или «я сейчас
        // здесь»: снимки образцов делаются дома и координат не несут
        // (ТЗ 9.1, источник 1).
        coord_source: num(latitude) !== null ? ('manual' as const) : null,
        coord_precision_m: num(precision),
        companions: text(companions),
        notes: text(notes),
      };
      const saved = id ? await api.updateTrip(id, body) : await api.createTrip(body);
      navigate(`/trips/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
      setBusy(false);
    }
  }

  if (!loaded) {
    return (
      <div className="app">
        <div className="scroll muted small">Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Отмена">
          <Icon name="x" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">{editing ? 'Правка выезда' : 'Новый выезд'}</h2>
        <div className="spacer" />
        <button className="link-btn" disabled={busy} onClick={save}>
          {busy ? '…' : 'Сохранить'}
        </button>
      </div>

      <div className="scroll">
        {editing && specimenCount > 0 && (
          <div className="notice notice--warn">
            <Icon name="alert" className="icon icon-sm" />
            <span>
              К выезду привязано образцов: {specimenCount}. Правка места и координат
              изменит их положение сразу у всех.
            </span>
          </div>
        )}

        <div className="label-key">Название</div>
        <input
          className="field-input"
          value={title}
          placeholder="Додо, июнь 2024"
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="hint">Если пусто — соберётся из места и даты</div>

        <div className="label-key">Дата</div>
        <input
          className="field-input"
          type="date"
          value={startedOn}
          onChange={(e) => setStartedOn(e.target.value)}
        />

        <div className="label-key">Месторождение</div>
        <SuggestField
          value={localityName}
          onChange={setLocalityName}
          fetchSuggestions={localitySuggestions}
          placeholder="Додо"
        />
        <input
          className="field-input"
          style={{ marginTop: 'var(--space-2)' }}
          value={country}
          placeholder="страна"
          onChange={(e) => setCountry(e.target.value)}
        />
        <input
          className="field-input"
          style={{ marginTop: 'var(--space-2)' }}
          value={localityNote}
          placeholder="уточнение: горизонт, жила, отвал"
          onChange={(e) => setLocalityNote(e.target.value)}
        />

        <CoordPicker
          latitude={latitude}
          longitude={longitude}
          precisionM={precision}
          onChange={(next) => {
            setLatitude(next.latitude);
            setLongitude(next.longitude);
            setPrecision(next.precisionM);
          }}
        />
        <div className="hint">
          Место вводится один раз на всю партию: правка этой точки двигает на карте
          все образцы выезда
        </div>

        <div className="label-key">Спутники</div>
        <input
          className="field-input"
          value={companions}
          onChange={(e) => setCompanions(e.target.value)}
        />

        <div className="label-key">Заметки о поездке</div>
        <textarea
          className="field-input field-area"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {error && (
          <div className="notice notice--danger" style={{ marginTop: 'var(--space-4)' }}>
            {error}
          </div>
        )}

        <button
          className="btn btn--primary btn--full"
          style={{ marginTop: 'var(--space-5)' }}
          disabled={busy}
          onClick={save}
        >
          {busy ? 'Сохраняем…' : editing ? 'Сохранить' : 'Создать выезд'}
        </button>
      </div>
    </div>
  );
}
